// ── Imágenes pegadas en el editor de texto (2026-11-03) ──────────────────────
// El editor no conocía las imágenes: pegar una captura no hacía nada. Acá se
// decide qué del portapapeles es una imagen y se sube. El servidor la guarda y,
// al enviar el correo, la incrusta (services/imagenesPegadas.ts).

import { api } from './api.js';

/** Los mismos que acepta el servidor. SVG no: lleva scripts. */
const TIPOS_ADMITIDOS = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
export const MAX_BYTES_IMAGEN = 10 * 1024 * 1024;

export function esImagenAdmitida(archivo: File): boolean {
  return TIPOS_ADMITIDOS.has(archivo.type);
}

export async function subirImagen(archivo: File): Promise<string> {
  const datos = new FormData();
  datos.append('imagen', archivo);
  const { url } = await api.upload<{ url: string }>('/api/imagenes-pegadas', datos);
  return url;
}

function textoDeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('style, meta, link, title, script').forEach(n => { n.remove(); });
  return (doc.body.textContent ?? '').trim();
}

/**
 * Las imágenes del portapapeles, solo si lo que se copió ES una imagen.
 *
 * Word, Excel y Keynote copian el texto Y una foto de ese texto: con texto, lo
 * que se quiso pegar es el texto. Las dos excepciones son texto que no es
 * contenido: copiar un archivo del Finder trae su nombre, y "Copiar imagen" en
 * algunos navegadores trae su dirección.
 */
export function imagenesDelPortapapeles(datos: { files: ArrayLike<File>; getData(formato: string): string }): File[] {
  const imagenes = Array.from(datos.files).filter(f => f.type.startsWith('image/'));
  if (imagenes.length === 0) return [];

  const esContenido = (texto: string): boolean =>
    texto !== '' && !/^https?:\/\/\S+$/i.test(texto) && !imagenes.some(f => f.name === texto);
  const html = datos.getData('text/html');
  if (esContenido(datos.getData('text/plain').trim()) || (html && esContenido(textoDeHtml(html)))) return [];

  return imagenes;
}

/** Una dirección que el editor puede nombrar y que quien recibe el correo va a ver. */
function srcUtilizable(src: string): boolean {
  return /^https?:\/\//i.test(src) || src.startsWith('/storage/');
}

/** HTML pegado con imágenes que no se pueden dejar como están (`data:`, `file:`, `cid:`). */
export function tieneImagenesPorResolver(html: string): boolean {
  if (!/<img\b/i.test(html)) return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return [...doc.querySelectorAll('img')].some(img => !srcUtilizable(img.getAttribute('src') ?? ''));
}

function dataUrlAArchivo(src: string): File | null {
  const m = /^data:(image\/[a-z+.-]+);base64,([\s\S]+)$/i.exec(src);
  if (!m) return null;
  const tipo = m[1]!.toLowerCase();
  try {
    const bytes = Uint8Array.from(atob(m[2]!.replace(/\s/g, '')), c => c.charCodeAt(0));
    const archivo = new File([bytes], `imagen.${tipo.split('/')[1] ?? 'png'}`, { type: tipo });
    return esImagenAdmitida(archivo) && archivo.size <= MAX_BYTES_IMAGEN ? archivo : null;
  } catch {
    return null;
  }
}

/**
 * Sube las imágenes `data:` que vienen adentro del HTML pegado (Outlook, Docs,
 * otro correo) y cambia su dirección por la nuestra. Las que apuntan a un archivo
 * de la computadora (`file:`, Word) o a un adjunto de otro correo (`cid:`) no se
 * pueden traer: se sacan y se cuentan, para decirlo en vez de dejar un cuadro roto.
 */
export async function resolverImagenesDelHtml(
  html: string,
  subir: (archivo: File) => Promise<string> = subirImagen,
): Promise<{ html: string; perdidas: number }> {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let perdidas = 0;
  for (const img of [...doc.querySelectorAll('img')]) {
    const src = img.getAttribute('src') ?? '';
    if (srcUtilizable(src)) continue;
    const archivo = dataUrlAArchivo(src);
    try {
      if (!archivo) throw new Error('no se puede traer');
      img.setAttribute('src', await subir(archivo));
    } catch {
      img.remove();
      perdidas++;
    }
  }
  return { html: doc.body.innerHTML, perdidas };
}
